import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  Container, 
  Grid, 
  Paper, 
  Typography, 
  Box, 
  Tabs, 
  Tab, 
  Button,
  Menu,
  MenuItem,
  IconButton,
  Divider,
  FormControl,
  InputLabel,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  TextField
} from '@mui/material';
import { 
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Bar, Pie, Line } from 'react-chartjs-2';
import 'chartjs-adapter-dayjs-4';
import dayjs from 'dayjs';
import 'dayjs/locale/de';
import { 
  FileDownload as DownloadIcon,
  FilterAlt as FilterIcon,
  Print as PrintIcon,
  Visibility as ViewIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { fetchRides } from '../../redux/slices/ridesSlice';
import { fetchDrivers } from '../../redux/slices/driversSlice';
import { fetchOperators } from '../../redux/slices/operatorsSlice';
import { isCentralOperationsRole, isOperatorRole } from '../../redux/slices/authSlice';
import {
  addWorkTimeBreak,
  deleteWorkTimeBreak,
  fetchWorkTimeRows,
  formatMinutes,
  summarizeWorkTimeRows,
  updateWorkTimeBreak,
  updateWorkTimeShift
} from '../../services/workTimeService';
import { jsPDF } from 'jspdf';

// Dayjs auf Deutsch einstellen
dayjs.locale('de');

// ChartJS registrieren
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Konstanten für die Datumsbereich-Filter
const FILTER_DAY = 'day';
const FILTER_WEEK = 'week';
const FILTER_MONTH = 'month';
const FILTER_LAST_MONTH = 'last_month';
const FILTER_QUARTER = 'quarter';
const FILTER_YEAR = 'year';
const FILTER_CUSTOM = 'custom';

// Tabs
const TAB_OVERVIEW = 0;
const TAB_RIDES = 1;
const TAB_DRIVERS = 2;
const TAB_WORK_TIMES = 3;
const TAB_REVENUE = 4;
const TAB_OPERATORS = 5;
const CENTRAL_CREATED_OPERATOR_ID = '__central__';

const CENTRAL_ROLES = new Set(['admin', 'super_admin', 'central_admin', 'dispatcher', 'central_dispatch']);
const NON_OPERATOR_CREATED_ROLES = new Set(['customer', 'driver', 'system']);

const getCreatedByCentralName = (ride) => {
  if (ride.createdByOperatorId || ride.preferredOperatorId) {
    return null;
  }

  if (CENTRAL_ROLES.has(ride.createdByRole)) {
    const userName = ride.createdByUserName || ride.createdByName || ride.createdByEmail || '';
    return userName ? `Hauptzentrale: ${userName}` : 'Hauptzentrale';
  }

  if (
    NON_OPERATOR_CREATED_ROLES.has(ride.createdByRole) ||
    ride.dispatchScope === 'central_pool' ||
    !ride.createdByRole
  ) {
    return 'Hauptzentrale';
  }

  return null;
};

const getMediationLabel = (mediationType) => {
  switch (mediationType) {
    case 'cross_operator':
      return 'Vermittelt';
    case 'same_operator':
      return 'Eigen';
    case 'central':
      return 'Zentrale';
    default:
      return 'Unklar';
  }
};

const getDriverDisplayName = (driver) => (
  driver?.name ||
  driver?.displayName ||
  driver?.user?.name ||
  driver?.user?.displayName ||
  driver?.email ||
  driver?.user?.email ||
  driver?.id
);

const isDriverReportRecord = (driver) => driver?.isDriver !== false;

const toDateTimeInputValue = (date) => {
  if (!date) return '';
  const value = new Date(date);
  if (Number.isNaN(value.getTime())) return '';
  const pad = (part) => String(part).padStart(2, '0');
  return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}T${pad(value.getHours())}:${pad(value.getMinutes())}`;
};

const toDateInputValue = (date) => {
  if (!date) return '';
  const value = new Date(date);
  if (Number.isNaN(value.getTime())) return '';
  const pad = (part) => String(part).padStart(2, '0');
  return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`;
};

const createBreakFormFromBreak = (breakItem) => ({
  type: breakItem?.type || 'Pause',
  startTime: toDateTimeInputValue(breakItem?.startTime || new Date()),
  endTime: toDateTimeInputValue(breakItem?.endTime || new Date(Date.now() + 30 * 60000)),
  correctionReason: breakItem?.correctionReason || ''
});

const createShiftFormFromRow = (row) => ({
  shiftStart: toDateTimeInputValue(row?.shiftStart || new Date()),
  shiftEnd: toDateTimeInputValue(row?.shiftEnd || row?.effectiveEnd || new Date(Date.now() + 8 * 60 * 60000)),
  correctionReason: ''
});

/**
 * ReportsPage-Komponente
 * 
 * Zeigt verschiedene Berichte und Visualisierungen für Fahrten, Fahrer und Umsätze an.
 */
const ReportsPage = () => {
  const dispatch = useDispatch();
  const [currentTab, setCurrentTab] = useState(TAB_OVERVIEW);
  const [dateRange, setDateRange] = useState(FILTER_MONTH);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [customRangeDialogOpen, setCustomRangeDialogOpen] = useState(false);
  const [customRangeDraftStart, setCustomRangeDraftStart] = useState('');
  const [customRangeDraftEnd, setCustomRangeDraftEnd] = useState('');
  const [selectedDriverId, setSelectedDriverId] = useState('all');
  const [selectedCreatedOperatorId, setSelectedCreatedOperatorId] = useState('all');
  const [selectedDriverOperatorId, setSelectedDriverOperatorId] = useState('all');
  const [selectedMediationStatus, setSelectedMediationStatus] = useState('all');
  const [workTimeRows, setWorkTimeRows] = useState([]);
  const [workTimeLoading, setWorkTimeLoading] = useState(false);
  const [workTimeError, setWorkTimeError] = useState(null);
  const [selectedWorkTimeDriverId, setSelectedWorkTimeDriverId] = useState('all');
  const [selectedWorkTimeStatus, setSelectedWorkTimeStatus] = useState('all');
  const [selectedWorkTimeBreakType, setSelectedWorkTimeBreakType] = useState('all');
  const [selectedWorkTimeRow, setSelectedWorkTimeRow] = useState(null);
  const [breakDialogOpen, setBreakDialogOpen] = useState(false);
  const [editingBreak, setEditingBreak] = useState(null);
  const [breakForm, setBreakForm] = useState(createBreakFormFromBreak());
  const [breakActionError, setBreakActionError] = useState(null);
  const [breakSaving, setBreakSaving] = useState(false);
  const [shiftDialogOpen, setShiftDialogOpen] = useState(false);
  const [shiftForm, setShiftForm] = useState(createShiftFormFromRow());
  const [shiftActionError, setShiftActionError] = useState(null);
  const [shiftSaving, setShiftSaving] = useState(false);
  
  // Daten aus dem Redux Store abfragen
  const currentUser = useSelector(state => state.auth.user);
  const { rides, isLoading: ridesLoading, error: ridesError } = useSelector(state => state.rides);
  const { drivers, isLoading: driversLoading, error: driversError } = useSelector(state => state.drivers);
  const { operators = [], isLoading: operatorsLoading, error: operatorsError } = useSelector(state => state.operators || {});
  
  const isLoading = ridesLoading || driversLoading || operatorsLoading;
  const error = ridesError || driversError || operatorsError;
  
  // Startdatum für den gewählten Zeitraum – stabil memoized
  const getDateRangeStart = useCallback(() => {
    const now = new Date();
    switch (dateRange) {
      case FILTER_DAY:
        now.setHours(0, 0, 0, 0);
        return now;
      case FILTER_WEEK: {
        const dayOfWeek = now.getDay();
        const diff = now.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
        now.setDate(diff);
        now.setHours(0, 0, 0, 0);
        return now;
      }
      case FILTER_MONTH:
        now.setDate(1);
        now.setHours(0, 0, 0, 0);
        return now;
      case FILTER_LAST_MONTH:
        now.setMonth(now.getMonth() - 1);
        now.setDate(1);
        now.setHours(0, 0, 0, 0);
        return now;
      case FILTER_QUARTER: {
        const quarter = Math.floor(now.getMonth() / 3);
        now.setMonth(quarter * 3);
        now.setDate(1);
        now.setHours(0, 0, 0, 0);
        return now;
      }
      case FILTER_YEAR:
        now.setMonth(0);
        now.setDate(1);
        now.setHours(0, 0, 0, 0);
        return now;
      case FILTER_CUSTOM:
        return customStartDate
          ? new Date(`${customStartDate}T00:00:00`)
          : new Date(now.getFullYear(), now.getMonth(), 1);
      default:
        now.setDate(1);
        now.setHours(0, 0, 0, 0);
        return now;
    }
  }, [customStartDate, dateRange]);

  // Überschriftentitel – nur neu berechnet wenn dateRange sich ändert
  const dateRangeTitle = useMemo(() => {
    const startDate = getDateRangeStart();
    const options = { month: 'long', year: 'numeric' };
    switch (dateRange) {
      case FILTER_DAY:
        return startDate.toLocaleDateString('de-DE', { day: 'numeric', month: 'long', year: 'numeric' });
      case FILTER_WEEK: {
        const endOfWeek = new Date(startDate);
        endOfWeek.setDate(endOfWeek.getDate() + 6);
        return `${startDate.toLocaleDateString('de-DE', { day: 'numeric' })} - ${endOfWeek.toLocaleDateString('de-DE', { day: 'numeric', month: 'long', year: 'numeric' })}`;
      }
      case FILTER_MONTH:
        return startDate.toLocaleDateString('de-DE', options);
      case FILTER_LAST_MONTH:
        return `Letzter Monat: ${startDate.toLocaleDateString('de-DE', options)}`;
      case FILTER_QUARTER: {
        const quarter = Math.floor(startDate.getMonth() / 3) + 1;
        return `Q${quarter} ${startDate.getFullYear()}`;
      }
      case FILTER_YEAR:
        return `${startDate.getFullYear()}`;
      case FILTER_CUSTOM: {
        const endDate = customEndDate ? new Date(`${customEndDate}T23:59:59`) : startDate;
        return `${startDate.toLocaleDateString('de-DE')} - ${endDate.toLocaleDateString('de-DE')}`;
      }
      default:
        return startDate.toLocaleDateString('de-DE', options);
    }
  }, [customEndDate, dateRange, getDateRangeStart]);

  const reportBounds = useMemo(() => {
    const start = getDateRangeStart();
    const end = new Date();
    if (dateRange === FILTER_DAY) {
      end.setHours(23, 59, 59, 999);
    } else if (dateRange === FILTER_WEEK) {
      end.setTime(start.getTime());
      end.setDate(end.getDate() + 6);
      end.setHours(23, 59, 59, 999);
    } else if (dateRange === FILTER_MONTH) {
      end.setFullYear(start.getFullYear(), start.getMonth() + 1, 0);
      end.setHours(23, 59, 59, 999);
    } else if (dateRange === FILTER_LAST_MONTH) {
      end.setFullYear(start.getFullYear(), start.getMonth() + 1, 0);
      end.setHours(23, 59, 59, 999);
    } else if (dateRange === FILTER_QUARTER) {
      end.setTime(start.getTime());
      end.setMonth(end.getMonth() + 3);
      end.setDate(0);
      end.setHours(23, 59, 59, 999);
    } else if (dateRange === FILTER_YEAR) {
      end.setFullYear(start.getFullYear(), 11, 31);
      end.setHours(23, 59, 59, 999);
    } else if (dateRange === FILTER_CUSTOM && customEndDate) {
      end.setTime(new Date(`${customEndDate}T23:59:59.999`).getTime());
    }
    return { start, end };
  }, [customEndDate, dateRange, getDateRangeStart]);

  // Daten laden
  useEffect(() => {
    const loadData = async () => {
      try {
        await dispatch(fetchRides({
          status: ['open', 'accepted', 'in_progress', 'arrived', 'scheduled',
                  'pre_accepted', 'completed', 'cancelled', 'cancelled_by_driver', 'archived'],
          limit: 1000
        }));
        await dispatch(fetchDrivers());
        await dispatch(fetchOperators());
      } catch (error) {
        console.error('Fehler beim Laden der Berichtsdaten:', error);
      }
    };
    loadData();
  }, [dispatch, dateRange, customStartDate, customEndDate]);

  const handleTabChange = useCallback((event, newValue) => {
    setCurrentTab(newValue);
  }, []);

  const handleFilterClick = useCallback((event) => {
    setFilterAnchorEl(event.currentTarget);
  }, []);

  const handleFilterClose = useCallback(() => {
    setFilterAnchorEl(null);
  }, []);

  const handleFilterSelect = useCallback((range) => {
    if (range === FILTER_CUSTOM) {
      const currentStart = customStartDate || toDateInputValue(reportBounds.start);
      const currentEnd = customEndDate || toDateInputValue(reportBounds.end);
      setCustomRangeDraftStart(currentStart);
      setCustomRangeDraftEnd(currentEnd);
      setCustomRangeDialogOpen(true);
      setFilterAnchorEl(null);
      return;
    }
    setDateRange(range);
    setFilterAnchorEl(null);
  }, [customEndDate, customStartDate, reportBounds]);

  const customRangeDraftInvalid = useMemo(() => {
    if (!customRangeDraftStart || !customRangeDraftEnd) {
      return true;
    }
    return new Date(`${customRangeDraftStart}T00:00:00`) > new Date(`${customRangeDraftEnd}T23:59:59`);
  }, [customRangeDraftEnd, customRangeDraftStart]);

  const handleCloseCustomRangeDialog = useCallback(() => {
    setCustomRangeDialogOpen(false);
  }, []);

  const handleApplyCustomRange = useCallback(() => {
    if (customRangeDraftInvalid) return;
    setCustomStartDate(customRangeDraftStart);
    setCustomEndDate(customRangeDraftEnd);
    setDateRange(FILTER_CUSTOM);
    setCustomRangeDialogOpen(false);
  }, [customRangeDraftEnd, customRangeDraftInvalid, customRangeDraftStart]);

  const reloadWorkTimes = useCallback(async () => {
    setWorkTimeLoading(true);
    setWorkTimeError(null);

    try {
      const rows = await fetchWorkTimeRows({
        startDate: reportBounds.start,
        endDate: reportBounds.end
      });
      setWorkTimeRows(rows);
      return rows;
    } catch (error) {
      console.error('Fehler beim Laden der Arbeitszeiten:', error);
      setWorkTimeError(error.message || 'Arbeitszeiten konnten nicht geladen werden.');
      setWorkTimeRows([]);
      return [];
    } finally {
      setWorkTimeLoading(false);
    }
  }, [reportBounds]);

  useEffect(() => {
    if (currentTab !== TAB_WORK_TIMES) return undefined;
    let cancelled = false;
    reloadWorkTimes().then(() => {
      if (cancelled) return;
    });
    return () => {
      cancelled = true;
    };
  }, [currentTab, reloadWorkTimes]);

  const toDate = useCallback((value) => {
    if (!value) return null;
    if (value instanceof Date) return value;
    if (value.toDate) return value.toDate();
    if (value.seconds) return new Date(value.seconds * 1000);
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }, []);

  const getRideReferenceDate = useCallback((ride) => (
    toDate(ride.completedTimestamp) ||
    toDate(ride.completionTimestamp) ||
    toDate(ride.passengerPickedUpTimestamp) ||
    toDate(ride.timestamp)
  ), [toDate]);

  const formatDateTime = useCallback((value) => {
    const date = toDate(value);
    return date ? date.toLocaleString('de-DE') : '-';
  }, [toDate]);

  const formatMoney = useCallback((value) => {
    if (value === null || value === undefined || value === '') return '-';
    return `${Number(value).toFixed(2)} €`;
  }, []);

  const getBillableFare = useCallback((ride) => {
    const actual = ride.fare;
    const estimated = ride.estimatedEarningEur;
    return actual !== null && actual !== undefined ? Number(actual) :
      estimated !== null && estimated !== undefined ? Number(estimated) : 0;
  }, []);

  const completedReportRides = useMemo(() => {
    const { start, end } = reportBounds;
    return rides.filter((ride) => {
      if (ride.status !== 'completed' && ride.status !== 'archived') return false;
      const refDate = getRideReferenceDate(ride);
      return refDate && refDate >= start && refDate <= end;
    });
  }, [rides, reportBounds, getRideReferenceDate]);

  const operatorNameById = useMemo(() => {
    return new Map(operators.map(operator => [operator.id, operator.name || operator.id]));
  }, [operators]);

  const driverReportRows = useMemo(() => {
    return completedReportRides
      .filter(ride => selectedDriverId === 'all' || ride.driverId === selectedDriverId)
      .map(ride => ({
        ...ride,
        reportType: ride.rideSource === 'driver_walk_in' || ride.walkInRide ? 'Einsteiger-Tour' : 'App-/Portal-Fahrt',
        refDate: getRideReferenceDate(ride),
        billableFare: getBillableFare(ride),
        fareSource: ride.fare !== null && ride.fare !== undefined ? 'Tatsächlich' :
          ride.estimatedEarningEur !== null && ride.estimatedEarningEur !== undefined ? 'Geschätzt' : 'Fehlt'
      }))
      .sort((a, b) => (b.refDate?.getTime() || 0) - (a.refDate?.getTime() || 0));
  }, [completedReportRides, selectedDriverId, getRideReferenceDate, getBillableFare]);

  const operatorReportRows = useMemo(() => {
    return completedReportRides
      .map(ride => {
        const centralCreatedByName = getCreatedByCentralName(ride);
        const createdOperatorId = ride.createdByOperatorId || ride.preferredOperatorId || (centralCreatedByName ? CENTRAL_CREATED_OPERATOR_ID : '');
        const rideHasAssignedDriverOrVehicle = Boolean(ride.driverId || ride.driverVehicleId || ride.driverName || ride.driverLicensePlate);
        const driverOperatorId = ride.driverOperatorId || ride.vehicleOperatorId || (rideHasAssignedDriverOrVehicle ? CENTRAL_CREATED_OPERATOR_ID : '');
        const driverOperatorName = driverOperatorId === CENTRAL_CREATED_OPERATOR_ID
          ? 'Hauptzentrale'
          : ride.driverOperatorName || operatorNameById.get(driverOperatorId) || driverOperatorId || 'Nicht zugeordnet';
        const mediationType = createdOperatorId === CENTRAL_CREATED_OPERATOR_ID
          ? 'central'
          : !createdOperatorId || !driverOperatorId
          ? 'unknown'
          : createdOperatorId === driverOperatorId
            ? 'same_operator'
            : 'cross_operator';
        return {
          ...ride,
          createdOperatorId,
          createdOperatorName: ride.createdByOperatorName || ride.preferredOperatorName || centralCreatedByName || createdOperatorId || 'Zentrale / unbekannt',
          driverOperatorId,
          driverOperatorName,
          mediationType,
          refDate: getRideReferenceDate(ride),
          billableFare: getBillableFare(ride),
          fareSource: ride.fare !== null && ride.fare !== undefined ? 'Tatsächlich' :
            ride.estimatedEarningEur !== null && ride.estimatedEarningEur !== undefined ? 'Geschätzt' : 'Fehlt'
        };
      })
      .filter(ride => selectedCreatedOperatorId === 'all' || ride.createdOperatorId === selectedCreatedOperatorId)
      .filter(ride => selectedDriverOperatorId === 'all' || ride.driverOperatorId === selectedDriverOperatorId)
      .filter(ride => selectedMediationStatus === 'all' || ride.mediationType === selectedMediationStatus)
      .sort((a, b) => (b.refDate?.getTime() || 0) - (a.refDate?.getTime() || 0));
  }, [
    completedReportRides,
    operatorNameById,
    selectedCreatedOperatorId,
    selectedDriverOperatorId,
    selectedMediationStatus,
    getRideReferenceDate,
    getBillableFare
  ]);

  const driverNameById = useMemo(() => {
    return new Map(drivers.map(driver => [
      driver.id,
      getDriverDisplayName(driver)
    ]));
  }, [drivers]);

  const workTimeDriverOptions = useMemo(() => {
    return drivers
      .filter(isDriverReportRecord)
      .filter(driver => {
        if (isOperatorRole(currentUser)) {
          return driver.operatorId === currentUser?.operatorId;
        }
        if (isCentralOperationsRole(currentUser)) {
          return !driver.operatorId;
        }
        return false;
      })
      .sort((a, b) => (getDriverDisplayName(a) || '').localeCompare(getDriverDisplayName(b) || '', 'de'));
  }, [drivers, currentUser]);

  const workTimeDriverIdsInScope = useMemo(() => (
    new Set(workTimeDriverOptions.map(driver => driver.id))
  ), [workTimeDriverOptions]);

  const workTimeBreakTypes = useMemo(() => {
    return Array.from(new Set(
      workTimeRows.flatMap(row => row.breakTypes || []).filter(Boolean)
    )).sort((a, b) => a.localeCompare(b, 'de'));
  }, [workTimeRows]);

  const filteredWorkTimeRows = useMemo(() => {
    return workTimeRows
      .map(row => ({
        ...row,
        displayDriverName: driverNameById.get(row.driverId) || row.driverName || row.driverId
      }))
      .filter(row => {
        if (workTimeDriverIdsInScope.has(row.driverId)) return true;
        if (isOperatorRole(currentUser)) return row.operatorId === currentUser?.operatorId;
        if (isCentralOperationsRole(currentUser)) return !row.operatorId;
        return false;
      })
      .filter(row => selectedWorkTimeDriverId === 'all' || row.driverId === selectedWorkTimeDriverId)
      .filter(row => {
        if (selectedWorkTimeStatus === 'all') return true;
        if (selectedWorkTimeStatus === 'active') return row.isActive;
        if (selectedWorkTimeStatus === 'completed') return !row.isActive;
        if (selectedWorkTimeStatus === 'overtime') return row.overtimeMinutes > 0;
        return true;
      })
      .filter(row => (
        selectedWorkTimeBreakType === 'all' ||
        (row.breakTypes || []).includes(selectedWorkTimeBreakType)
      ));
  }, [
    workTimeRows,
    driverNameById,
    workTimeDriverIdsInScope,
    currentUser,
    selectedWorkTimeDriverId,
    selectedWorkTimeStatus,
    selectedWorkTimeBreakType
  ]);

  const workTimeSummary = useMemo(
    () => summarizeWorkTimeRows(filteredWorkTimeRows),
    [filteredWorkTimeRows]
  );

  const withWorkTimeDisplayName = useCallback((row) => row ? ({
    ...row,
    displayDriverName: driverNameById.get(row.driverId) || row.driverName || row.driverId
  }) : null, [driverNameById]);

  const refreshSelectedWorkTimeRow = useCallback(async (rowId) => {
    const rows = await reloadWorkTimes();
    const updatedRow = rows.find(row => row.id === rowId);
    setSelectedWorkTimeRow(withWorkTimeDisplayName(updatedRow));
  }, [reloadWorkTimes, withWorkTimeDisplayName]);

  const handleOpenWorkTimeDetails = useCallback((row) => {
    setSelectedWorkTimeRow(row);
    setBreakActionError(null);
  }, []);

  const handleCloseWorkTimeDetails = useCallback(() => {
    setSelectedWorkTimeRow(null);
    setBreakActionError(null);
    setShiftActionError(null);
  }, []);

  const handleOpenShiftDialog = useCallback((row) => {
    setSelectedWorkTimeRow(row);
    setShiftForm(createShiftFormFromRow(row));
    setShiftActionError(null);
    setShiftDialogOpen(true);
  }, []);

  const handleCloseShiftDialog = useCallback(() => {
    setShiftDialogOpen(false);
    setShiftActionError(null);
  }, []);

  const handleShiftFormChange = useCallback((field, value) => {
    setShiftForm(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleSaveShift = useCallback(async () => {
    if (!selectedWorkTimeRow) return;
    if (!shiftForm.correctionReason.trim()) {
      setShiftActionError('Bitte einen Grund für die Änderung angeben.');
      return;
    }

    setShiftSaving(true);
    setShiftActionError(null);

    try {
      await updateWorkTimeShift({
        dateKey: selectedWorkTimeRow.dateKey,
        driverId: selectedWorkTimeRow.driverId,
        shiftNumber: selectedWorkTimeRow.shiftNumber,
        shiftIndex: selectedWorkTimeRow.shiftIndex,
        shiftData: {
          shiftStart: new Date(shiftForm.shiftStart),
          shiftEnd: new Date(shiftForm.shiftEnd),
          correctionReason: shiftForm.correctionReason.trim(),
          source: 'web_admin'
        }
      });
      setShiftDialogOpen(false);
      await refreshSelectedWorkTimeRow(selectedWorkTimeRow.id);
    } catch (error) {
      setShiftActionError(error.message || 'Schichtzeiten konnten nicht gespeichert werden.');
    } finally {
      setShiftSaving(false);
    }
  }, [refreshSelectedWorkTimeRow, selectedWorkTimeRow, shiftForm]);

  const handleOpenAddBreak = useCallback((row) => {
    const startTime = row.isActive ? new Date() : row.shiftStart;
    const endCandidate = new Date(startTime.getTime() + 30 * 60000);
    const endTime = row.effectiveEnd && endCandidate > row.effectiveEnd ? row.effectiveEnd : endCandidate;

    setSelectedWorkTimeRow(row);
    setEditingBreak(null);
    setBreakForm({
      type: 'Pause',
      startTime: toDateTimeInputValue(startTime),
      endTime: toDateTimeInputValue(endTime),
      correctionReason: ''
    });
    setBreakActionError(null);
    setBreakDialogOpen(true);
  }, []);

  const handleOpenEditBreak = useCallback((breakItem) => {
    setEditingBreak(breakItem);
    setBreakForm(createBreakFormFromBreak(breakItem));
    setBreakActionError(null);
    setBreakDialogOpen(true);
  }, []);

  const handleCloseBreakDialog = useCallback(() => {
    setBreakDialogOpen(false);
    setEditingBreak(null);
    setBreakActionError(null);
  }, []);

  const handleBreakFormChange = useCallback((field, value) => {
    setBreakForm(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleSaveBreak = useCallback(async () => {
    if (!selectedWorkTimeRow) return;
    if (!breakForm.correctionReason.trim()) {
      setBreakActionError('Bitte einen Grund für die Änderung angeben.');
      return;
    }

    setBreakSaving(true);
    setBreakActionError(null);

    const payload = {
      dateKey: selectedWorkTimeRow.dateKey,
      driverId: selectedWorkTimeRow.driverId,
      shiftNumber: selectedWorkTimeRow.shiftNumber,
      shiftIndex: selectedWorkTimeRow.shiftIndex,
      breakData: {
        type: breakForm.type,
        startTime: new Date(breakForm.startTime),
        endTime: new Date(breakForm.endTime),
        correctionReason: breakForm.correctionReason.trim(),
        source: 'web_admin'
      }
    };

    try {
      if (editingBreak) {
        await updateWorkTimeBreak({
          ...payload,
          breakIndex: editingBreak.breakIndex
        });
      } else {
        await addWorkTimeBreak(payload);
      }
      setBreakDialogOpen(false);
      setEditingBreak(null);
      await refreshSelectedWorkTimeRow(selectedWorkTimeRow.id);
    } catch (error) {
      setBreakActionError(error.message || 'Pause konnte nicht gespeichert werden.');
    } finally {
      setBreakSaving(false);
    }
  }, [breakForm, editingBreak, refreshSelectedWorkTimeRow, selectedWorkTimeRow]);

  const handleDeleteBreak = useCallback(async (breakItem) => {
    if (!selectedWorkTimeRow || !breakItem) return;
    const confirmed = window.confirm(`Pause "${breakItem.type}" wirklich löschen?`);
    if (!confirmed) return;

    setBreakSaving(true);
    setBreakActionError(null);

    try {
      await deleteWorkTimeBreak({
        dateKey: selectedWorkTimeRow.dateKey,
        driverId: selectedWorkTimeRow.driverId,
        shiftNumber: selectedWorkTimeRow.shiftNumber,
        shiftIndex: selectedWorkTimeRow.shiftIndex,
        breakIndex: breakItem.breakIndex
      });
      await refreshSelectedWorkTimeRow(selectedWorkTimeRow.id);
    } catch (error) {
      setBreakActionError(error.message || 'Pause konnte nicht gelöscht werden.');
    } finally {
      setBreakSaving(false);
    }
  }, [refreshSelectedWorkTimeRow, selectedWorkTimeRow]);

  const renderWorkTimeDialogs = () => (
    <>
      <Dialog
        open={Boolean(selectedWorkTimeRow)}
        onClose={handleCloseWorkTimeDetails}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Schichtdetails
        </DialogTitle>
        <DialogContent dividers>
          {selectedWorkTimeRow && (
            <Box>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" color="text.secondary">Fahrer</Typography>
                  <Typography variant="body1">{selectedWorkTimeRow.displayDriverName}</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" color="text.secondary">Datum / Schicht</Typography>
                  <Typography variant="body1">{selectedWorkTimeRow.dateKey} / #{selectedWorkTimeRow.shiftNumber}</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" color="text.secondary">Status</Typography>
                  <Typography variant="body1">{selectedWorkTimeRow.isActive ? 'Aktiv' : 'Abgeschlossen'}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">Start</Typography>
                  <Typography variant="body1">{formatDateTime(selectedWorkTimeRow.shiftStart)}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">Ende</Typography>
                  <Typography variant="body1">
                    {selectedWorkTimeRow.isActive ? 'läuft' : formatDateTime(selectedWorkTimeRow.shiftEnd)}
                  </Typography>
                </Grid>
              </Grid>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} md={2.4}>
                  <Paper variant="outlined" sx={{ p: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">Brutto</Typography>
                    <Typography variant="body1" fontWeight={700}>{formatMinutes(selectedWorkTimeRow.grossMinutes)}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} md={2.4}>
                  <Paper variant="outlined" sx={{ p: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">Pausen abgezogen</Typography>
                    <Typography variant="body1" fontWeight={700}>{formatMinutes(selectedWorkTimeRow.breakMinutes)}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} md={2.4}>
                  <Paper variant="outlined" sx={{ p: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">Nicht abgezogen</Typography>
                    <Typography variant="body1" fontWeight={700}>{formatMinutes(selectedWorkTimeRow.nonDeductibleBreakMinutes || 0)}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} md={2.4}>
                  <Paper variant="outlined" sx={{ p: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">Offline</Typography>
                    <Typography variant="body1" fontWeight={700}>{formatMinutes(selectedWorkTimeRow.offlineMinutes)}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} md={2.4}>
                  <Paper variant="outlined" sx={{ p: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">Netto</Typography>
                    <Typography variant="body1" fontWeight={700}>{formatMinutes(selectedWorkTimeRow.netMinutes)}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} md={2.4}>
                  <Paper variant="outlined" sx={{ p: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">Überstunden</Typography>
                    <Typography variant="body1" fontWeight={700}>{formatMinutes(selectedWorkTimeRow.overtimeMinutes)}</Typography>
                  </Paper>
                </Grid>
              </Grid>

              {(breakActionError || shiftActionError) && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {breakActionError || shiftActionError}
                </Alert>
              )}

              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="h6">Schichtzeiten</Typography>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={() => handleOpenShiftDialog(selectedWorkTimeRow)}
                >
                  Zeiten korrigieren
                </Button>
              </Stack>

              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="h6">Pausen</Typography>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenAddBreak(selectedWorkTimeRow)}
                >
                  Pause hinzufügen
                </Button>
              </Stack>

              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Typ</TableCell>
                      <TableCell>Start</TableCell>
                      <TableCell>Ende</TableCell>
                      <TableCell align="right">Dauer</TableCell>
                      <TableCell>Wertung</TableCell>
                      <TableCell>Quelle</TableCell>
                      <TableCell>Grund</TableCell>
                      <TableCell align="right">Aktionen</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedWorkTimeRow.breaks.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center">Keine Pausen erfasst</TableCell>
                      </TableRow>
                    ) : (
                      selectedWorkTimeRow.breaks.map(breakItem => (
                        <TableRow key={breakItem.id}>
                          <TableCell>{breakItem.type}</TableCell>
                          <TableCell>{formatDateTime(breakItem.startTime)}</TableCell>
                          <TableCell>{formatDateTime(breakItem.endTime)}</TableCell>
                          <TableCell align="right">{formatMinutes(breakItem.minutes)}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={breakItem.isDeductible ? 'Abgezogen' : 'Nicht abgezogen'}
                              color={breakItem.isDeductible ? 'default' : 'info'}
                              variant={breakItem.isDeductible ? 'outlined' : 'filled'}
                            />
                          </TableCell>
                          <TableCell>{breakItem.source}</TableCell>
                          <TableCell>{breakItem.correctionReason || '-'}</TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              title="Pause bearbeiten"
                              onClick={() => handleOpenEditBreak(breakItem)}
                              disabled={breakSaving}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              title="Pause löschen"
                              onClick={() => handleDeleteBreak(breakItem)}
                              disabled={breakSaving}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseWorkTimeDetails}>Schließen</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={shiftDialogOpen}
        onClose={handleCloseShiftDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Schichtzeiten korrigieren</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Das Speichern setzt ein Schichtende und markiert die Schicht als abgeschlossen.
          </DialogContentText>
          {shiftActionError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {shiftActionError}
            </Alert>
          )}
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Schichtbeginn"
              type="datetime-local"
              value={shiftForm.shiftStart}
              onChange={(event) => handleShiftFormChange('shiftStart', event.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
            />
            <TextField
              label="Schichtende"
              type="datetime-local"
              value={shiftForm.shiftEnd}
              onChange={(event) => handleShiftFormChange('shiftEnd', event.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
            />
            <TextField
              label="Grund der Änderung"
              value={shiftForm.correctionReason}
              onChange={(event) => handleShiftFormChange('correctionReason', event.target.value)}
              fullWidth
              size="small"
              multiline
              minRows={2}
              required
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseShiftDialog} disabled={shiftSaving}>Abbrechen</Button>
          <Button onClick={handleSaveShift} variant="contained" disabled={shiftSaving}>
            Speichern
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={breakDialogOpen}
        onClose={handleCloseBreakDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{editingBreak ? 'Pause bearbeiten' : 'Pause hinzufügen'}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Änderungen werden direkt in der Schicht gespeichert und danach neu berechnet.
          </DialogContentText>
          {breakActionError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {breakActionError}
            </Alert>
          )}
          <Stack spacing={2} sx={{ pt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Pausentyp</InputLabel>
              <Select
                label="Pausentyp"
                value={breakForm.type}
                onChange={(event) => handleBreakFormChange('type', event.target.value)}
              >
                <MenuItem value="Pause">Pause</MenuItem>
                <MenuItem value="Stadtfahrt">Stadtfahrt</MenuItem>
                <MenuItem value="Langstrecke">Langstrecke</MenuItem>
                <MenuItem value="Disposition">Disposition</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Start"
              type="datetime-local"
              value={breakForm.startTime}
              onChange={(event) => handleBreakFormChange('startTime', event.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
            />
            <TextField
              label="Ende"
              type="datetime-local"
              value={breakForm.endTime}
              onChange={(event) => handleBreakFormChange('endTime', event.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
            />
            <TextField
              label="Grund der Änderung"
              value={breakForm.correctionReason}
              onChange={(event) => handleBreakFormChange('correctionReason', event.target.value)}
              fullWidth
              size="small"
              multiline
              minRows={2}
              required
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBreakDialog} disabled={breakSaving}>Abbrechen</Button>
          <Button onClick={handleSaveBreak} variant="contained" disabled={breakSaving}>
            Speichern
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );

  const buildCsv = useCallback((header, rows) => {
    const escape = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;
    return `\uFEFF${header.map(escape).join(';')}\n${rows.map(row => row.map(escape).join(';')).join('\n')}`;
  }, []);

  const downloadCsv = useCallback((filename, csv) => {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, []);

  const handleExportCsv = useCallback(() => {
    if (currentTab === TAB_WORK_TIMES) {
      const dateSuffix = `${reportBounds.start.toISOString().slice(0, 10)}_bis_${reportBounds.end.toISOString().slice(0, 10)}`;
      const header = ['Datum', 'Fahrer-ID', 'Fahrer', 'Schicht', 'Start', 'Ende', 'Status', 'Brutto-Minuten', 'Pausen-Minuten', 'Nicht-abgezogene-Minuten', 'Offline-Minuten', 'Netto-Minuten', 'Soll-Minuten', 'Überstunden-Minuten', 'Pausentypen'];
      const rows = filteredWorkTimeRows.map(row => [
        row.dateKey,
        row.driverId,
        row.displayDriverName,
        row.shiftNumber,
        formatDateTime(row.shiftStart),
        row.isActive ? 'laufend' : formatDateTime(row.shiftEnd),
        row.isActive ? 'Aktiv' : 'Abgeschlossen',
        row.grossMinutes,
        row.breakMinutes,
        row.nonDeductibleBreakMinutes || 0,
        row.offlineMinutes,
        row.netMinutes,
        row.targetMinutes,
        row.overtimeMinutes,
        (row.breakTypes || []).join(', ')
      ]);
      downloadCsv(`arbeitszeiten-${dateSuffix}.csv`, buildCsv(header, rows));
      return;
    }

    const dateSuffix = `${reportBounds.start.toISOString().slice(0, 10)}_bis_${reportBounds.end.toISOString().slice(0, 10)}`;
    if (currentTab === TAB_OPERATORS) {
      const header = ['Fahrt-ID', 'Datum', 'Auftraggeber-Unternehmer', 'Ausführender Unternehmer', 'Fahrer', 'Start', 'Ziel', 'Tatsächlicher Preis', 'Geschätzter Preis', 'Abrechnungsbetrag', 'Preisquelle', 'Vermittlungsart'];
      const rows = operatorReportRows.map(ride => [
        ride.id,
        formatDateTime(ride.refDate),
        ride.createdOperatorName,
        ride.driverOperatorName,
        ride.driverName || '',
        ride.start || '',
        ride.destination || '',
        ride.fare ?? '',
        ride.estimatedEarningEur ?? '',
        ride.billableFare,
        ride.fareSource,
        getMediationLabel(ride.mediationType)
      ]);
      downloadCsv(`unternehmer-vermittlungen-${dateSuffix}.csv`, buildCsv(header, rows));
      return;
    }

    const header = ['Fahrt-ID', 'Datum', 'Fahrer', 'Typ', 'Start', 'Ziel', 'Fahrzeug', 'Tatsächlicher Preis', 'Geschätzter Preis', 'Abrechnungsbetrag', 'Preisquelle'];
    const rows = driverReportRows.map(ride => [
      ride.id,
      formatDateTime(ride.refDate),
      ride.driverName || '',
      ride.reportType,
      ride.start || '',
      ride.destination || '',
      [ride.driverModel, ride.driverLicensePlate].filter(Boolean).join(' / '),
      ride.fare ?? '',
      ride.estimatedEarningEur ?? '',
      ride.billableFare,
      ride.fareSource
    ]);
    downloadCsv(`fahrerleistung-${dateSuffix}.csv`, buildCsv(header, rows));
  }, [currentTab, reportBounds, filteredWorkTimeRows, operatorReportRows, driverReportRows, formatDateTime, buildCsv, downloadCsv]);

  const handleExportPdf = useCallback(() => {
    const dateSuffix = `${reportBounds.start.toISOString().slice(0, 10)} bis ${reportBounds.end.toISOString().slice(0, 10)}`;
    const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const isOperatorReport = currentTab === TAB_OPERATORS;
    const rows = isOperatorReport ? operatorReportRows : driverReportRows;
    const total = rows.reduce((sum, ride) => sum + ride.billableFare, 0);
    let y = 14;

    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(16);
    pdf.text(isOperatorReport ? 'Unternehmer-Vermittlungsnachweis' : 'Fahrerleistung', 14, y);
    y += 8;
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);
    pdf.text(`Zeitraum: ${dateSuffix}`, 14, y);
    y += 6;
    pdf.text(`Fahrten: ${rows.length}   Summe: ${total.toFixed(2)} EUR`, 14, y);
    y += 10;

    const headers = isOperatorReport
      ? ['Datum', 'Auftraggeber', 'Ausführer', 'Fahrer', 'Betrag', 'Art']
      : ['Datum', 'Fahrer', 'Typ', 'Start', 'Ziel', 'Betrag'];
    const widths = isOperatorReport ? [34, 50, 50, 42, 24, 28] : [34, 42, 34, 58, 58, 24];

    pdf.setFont('helvetica', 'bold');
    let x = 14;
    headers.forEach((header, index) => {
      pdf.text(header, x, y);
      x += widths[index];
    });
    y += 5;
    pdf.setFont('helvetica', 'normal');

    rows.forEach((ride) => {
      if (y > 190) {
        pdf.addPage();
        y = 14;
      }
      const values = isOperatorReport
          ? [
            formatDateTime(ride.refDate),
            ride.createdOperatorName,
            ride.driverOperatorName,
            ride.driverName || '-',
            `${ride.billableFare.toFixed(2)} EUR`,
            getMediationLabel(ride.mediationType)
          ]
        : [
            formatDateTime(ride.refDate),
            ride.driverName || '-',
            ride.reportType,
            ride.start || '-',
            ride.destination || '-',
            `${ride.billableFare.toFixed(2)} EUR`
          ];
      x = 14;
      values.forEach((value, index) => {
        const text = pdf.splitTextToSize(String(value), widths[index] - 2)[0] || '';
        pdf.text(text, x, y);
        x += widths[index];
      });
      y += 6;
    });

    pdf.save(`${isOperatorReport ? 'unternehmer-nachweis' : 'fahrerleistung'}-${new Date().toISOString().slice(0, 10)}.pdf`);
  }, [currentTab, reportBounds, operatorReportRows, driverReportRows, formatDateTime]);

  const handlePrint = useCallback(() => {
    window.print();
  }, []);

  // Übersichtsdaten – nur neu berechnet wenn rides oder dateRange sich ändern.
  // Enthält O(n²)-Logik (filter in map), daher ist Memoization hier besonders wichtig.
  const overviewChartData = useMemo(() => {
    const totalRides = rides.length;
    const ridesByStatus = rides.reduce((acc, ride) => {
      acc[ride.status] = (acc[ride.status] || 0) + 1;
      return acc;
    }, {});

    const today = new Date();
    const daysToShow = dateRange === FILTER_WEEK ? 7 : 30;

    // Timestamps vorberechnen um wiederholte Date-Konstruktionen im inneren Loop zu vermeiden
    const dayBuckets = Array.from({ length: daysToShow }, (_, index) => {
      const start = new Date(today);
      start.setDate(start.getDate() - (daysToShow - 1 - index));
      start.setHours(0, 0, 0, 0);
      const end = new Date(start);
      end.setDate(end.getDate() + 1);
      return { date: start, startMs: start.getTime(), endMs: end.getTime() };
    });

    const completedRides = rides.filter(r => r.status === 'completed' || r.status === 'archived');
    const averageFare = completedRides.length > 0
      ? completedRides.reduce((sum, r) => sum + (r.estimatedEarningEur || r.fare || 0), 0) / completedRides.length
      : 0;

    const ridesByDay = dayBuckets.map(({ date, startMs, endMs }) => ({
      date,
      count: rides.filter(r => {
        if (!r.timestamp) return false;
        const ms = new Date(r.timestamp).getTime();
        return ms >= startMs && ms < endMs;
      }).length,
    }));

    const ratingsByDay = dayBuckets.map(({ date, startMs, endMs }) => {
      const dayRides = completedRides.filter(r => {
        if (!r.completionTimestamp) return false;
        const ms = new Date(r.completionTimestamp).getTime();
        return ms >= startMs && ms < endMs;
      });
      return {
        date,
        rating: dayRides.length > 0
          ? dayRides.reduce((sum, r) => sum + (r.rating || 0), 0) / dayRides.length
          : 0,
      };
    });

    return { totalRides, ridesByStatus, ridesByDay, averageFare, ratingsByDay };
  }, [rides, dateRange]);
  
  // Übersichts-Tab-Inhalt – nur neu gerendert wenn overviewChartData oder drivers sich ändern
  const renderOverviewTab = useMemo(() => {
    const {
      totalRides,
      ridesByStatus,
      ridesByDay,
      averageFare,
      ratingsByDay
    } = overviewChartData;
    
    // Daten für das Fahrtenstatus-Tortendiagramm
    const pieData = {
      labels: Object.keys(ridesByStatus).map(status => {
        switch (status) {
          case 'open': return 'Angefragt';
          case 'accepted': return 'Akzeptiert';
          case 'pre_accepted': return 'Vom Fahrer angenommen';
          case 'in_progress': return 'Zielfahrt';
          case 'arrived': return 'Angekommen';
          case 'completed': return 'Abgeschlossen';
          case 'archived': return 'Archiviert';
          case 'cancelled': return 'Storniert';
          case 'cancelled_by_driver': return 'Vom Fahrer storniert';
          case 'scheduled': return 'Geplant';
          default: return status;
        }
      }),
      datasets: [
        {
          data: Object.values(ridesByStatus),
          backgroundColor: [
            '#FFCE56', // Gelb für angefragt/offen
            '#36A2EB', // Blau für akzeptiert
            '#32a4a8', // Hellblau für vom Fahrer angenommen
            '#4BC0C0', // Türkis für Zielfahrt
            '#8bc34a', // Hellgrün für angekommen
            '#4CAF50', // Grün für abgeschlossen
            '#2E7D32', // Dunkelgrün für archiviert
            '#FF6384', // Rot für storniert
            '#d32f2f', // Dunkelrot für vom Fahrer storniert
            '#9C27B0'  // Lila für geplant
          ],
          borderWidth: 1
        }
      ]
    };
    
    // Optionen für das Tortendiagramm
    const pieOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'right',
        },
        title: {
          display: true,
          text: 'Fahrten nach Status'
        }
      }
    };
    
    // Daten für das Balkendiagramm der Fahrten pro Tag
    const barData = {
      labels: ridesByDay.map(data => data.date.toLocaleDateString('de-DE', { weekday: 'short', day: 'numeric', month: 'numeric' })),
      datasets: [
        {
          label: 'Anzahl Fahrten',
          data: ridesByDay.map(data => data.count),
          backgroundColor: '#FFD700', // Gold/Gelb für Taxis
          borderColor: '#212121', // Dunkelgrau
          borderWidth: 1
        }
      ]
    };
    
    // Optionen für das Balkendiagramm
    const barOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top',
        },
        title: {
          display: true,
          text: 'Fahrten pro Tag'
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          ticks: {
            precision: 0
          }
        }
      }
    };
    
    // Daten für das Lininiendiagramm der Bewertungen
    const lineData = {
      labels: ratingsByDay.map(data => data.date.toLocaleDateString('de-DE', { weekday: 'short', day: 'numeric', month: 'numeric' })),
      datasets: [
        {
          label: 'Durchschnittliche Bewertung',
          data: ratingsByDay.map(data => data.rating),
          backgroundColor: 'rgba(33, 33, 33, 0.2)',
          borderColor: '#212121',
          borderWidth: 2,
          tension: 0.3,
          fill: true
        }
      ]
    };
    
    // Optionen für das Liniendiagramm
    const lineOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top',
        },
        title: {
          display: true,
          text: 'Bewertungstrend'
        }
      },
      scales: {
        y: {
          beginAtZero: false,
          min: 0,
          max: 5,
          ticks: {
            stepSize: 1
          }
        }
      }
    };
    
    return (
      <Grid container spacing={3}>
        {/* KPI-Karten */}
        <Grid item xs={12} md={6} lg={3}>
          <Paper
            elevation={2}
            sx={{
              p: 2,
              textAlign: 'center',
              height: '100%',
              backgroundImage: 'linear-gradient(135deg, #FFD700 0%, #FFC107 100%)',
              color: '#212121'
            }}
          >
            <Typography variant="h3" component="div" sx={{ fontWeight: 'bold' }}>
              {totalRides}
            </Typography>
            <Typography variant="body1">
              Fahrten gesamt
            </Typography>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={6} lg={3}>
          <Paper
            elevation={2}
            sx={{
              p: 2,
              textAlign: 'center',
              height: '100%',
              backgroundImage: 'linear-gradient(135deg, #212121 0%, #424242 100%)',
              color: '#FFC107'
            }}
          >
            <Typography variant="h3" component="div" sx={{ fontWeight: 'bold' }}>
              {ridesByStatus['completed'] || 0}
            </Typography>
            <Typography variant="body1">
              Abgeschlossene Fahrten
            </Typography>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={6} lg={3}>
          <Paper
            elevation={2}
            sx={{
              p: 2,
              textAlign: 'center',
              height: '100%',
              backgroundImage: 'linear-gradient(135deg, #FFD700 0%, #FFC107 100%)',
              color: '#212121'
            }}
          >
            <Typography variant="h3" component="div" sx={{ fontWeight: 'bold' }}>
              {averageFare.toFixed(2)} €
            </Typography>
            <Typography variant="body1">
              Durchschnittlicher Fahrpreis
            </Typography>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={6} lg={3}>
          <Paper
            elevation={2}
            sx={{
              p: 2,
              textAlign: 'center',
              height: '100%',
              backgroundImage: 'linear-gradient(135deg, #212121 0%, #424242 100%)',
              color: '#FFC107'
            }}
          >
            <Typography variant="h3" component="div" sx={{ fontWeight: 'bold' }}>
              {drivers ? drivers.filter(isDriverReportRecord).length : 0}
            </Typography>
            <Typography variant="body1">
              Aktive Fahrer
            </Typography>
          </Paper>
        </Grid>
        
        {/* Diagramme */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: 300 }}>
            <Pie data={pieData} options={pieOptions} />
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: 300 }}>
            <Bar data={barData} options={barOptions} />
          </Paper>
        </Grid>
        
        <Grid item xs={12}>
          <Paper sx={{ p: 2, height: 300 }}>
            <Line data={lineData} options={lineOptions} />
          </Paper>
        </Grid>
      </Grid>
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overviewChartData, drivers]);

  // Fahrten-Tab-Inhalt
  const renderRidesTab = () => {
    // TODO: Detaillierte Fahrtenanalyse implementieren
    return (
      <Typography variant="body1">
        Fahrtenanalyse wird implementiert...
      </Typography>
    );
  };
  
  // Fahrer-Tab-Inhalt
  const renderDriversTab = () => {
    const total = driverReportRows.reduce((sum, ride) => sum + ride.billableFare, 0);
    const actualCount = driverReportRows.filter(ride => ride.fareSource === 'Tatsächlich').length;
    const walkInCount = driverReportRows.filter(ride => ride.reportType === 'Einsteiger-Tour').length;

    return (
      <Box>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 3 }}>
          <FormControl size="small" sx={{ minWidth: 260 }}>
            <InputLabel>Fahrer</InputLabel>
            <Select
              label="Fahrer"
              value={selectedDriverId}
              onChange={(event) => setSelectedDriverId(event.target.value)}
            >
              <MenuItem value="all">Alle Fahrer</MenuItem>
              {drivers.filter(isDriverReportRecord).map(driver => (
                <MenuItem key={driver.id} value={driver.id}>
                  {getDriverDisplayName(driver)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleExportCsv}>
            CSV exportieren
          </Button>
          <Button variant="outlined" startIcon={<PrintIcon />} onClick={handleExportPdf}>
            PDF exportieren
          </Button>
        </Stack>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">Fahrten</Typography>
              <Typography variant="h5">{driverReportRows.length}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={3}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">Umsatz</Typography>
              <Typography variant="h5">{formatMoney(total)}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={3}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">Tatsächliche Preise</Typography>
              <Typography variant="h5">{actualCount}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={3}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">Einsteiger-Touren</Typography>
              <Typography variant="h5">{walkInCount}</Typography>
            </Paper>
          </Grid>
        </Grid>

        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Datum</TableCell>
                <TableCell>Fahrer</TableCell>
                <TableCell>Typ</TableCell>
                <TableCell>Start</TableCell>
                <TableCell>Ziel</TableCell>
                <TableCell align="right">Betrag</TableCell>
                <TableCell>Preisquelle</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {driverReportRows.map(ride => (
                <TableRow key={ride.id}>
                  <TableCell>{formatDateTime(ride.refDate)}</TableCell>
                  <TableCell>{ride.driverName || '-'}</TableCell>
                  <TableCell>
                    <Chip size="small" label={ride.reportType} color={ride.reportType === 'Einsteiger-Tour' ? 'warning' : 'default'} />
                  </TableCell>
                  <TableCell>{ride.start || '-'}</TableCell>
                  <TableCell>{ride.destination || 'Kein Ziel angegeben'}</TableCell>
                  <TableCell align="right">{formatMoney(ride.billableFare)}</TableCell>
                  <TableCell>{ride.fareSource}</TableCell>
                </TableRow>
              ))}
              {driverReportRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center">Keine abgeschlossenen Fahrten im Zeitraum</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

      </Box>
    );
  };

  const renderWorkTimesTab = () => {
    return (
      <Box>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 3 }}>
          <FormControl size="small" sx={{ minWidth: 260 }}>
            <InputLabel>Fahrer</InputLabel>
            <Select
              label="Fahrer"
              value={selectedWorkTimeDriverId}
              onChange={(event) => setSelectedWorkTimeDriverId(event.target.value)}
            >
              <MenuItem value="all">Alle Fahrer</MenuItem>
              {workTimeDriverOptions.map(driver => (
                <MenuItem key={driver.id} value={driver.id}>
                  {getDriverDisplayName(driver)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 190 }}>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={selectedWorkTimeStatus}
              onChange={(event) => setSelectedWorkTimeStatus(event.target.value)}
            >
              <MenuItem value="all">Alle Schichten</MenuItem>
              <MenuItem value="active">Nur laufende</MenuItem>
              <MenuItem value="completed">Nur abgeschlossene</MenuItem>
              <MenuItem value="overtime">Nur Überstunden</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 190 }}>
            <InputLabel>Pausentyp</InputLabel>
            <Select
              label="Pausentyp"
              value={selectedWorkTimeBreakType}
              onChange={(event) => setSelectedWorkTimeBreakType(event.target.value)}
            >
              <MenuItem value="all">Alle Typen</MenuItem>
              {workTimeBreakTypes.map(type => (
                <MenuItem key={type} value={type}>{type}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleExportCsv}>
            CSV exportieren
          </Button>
        </Stack>

        {workTimeError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {workTimeError}
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={2}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">Schichten</Typography>
              <Typography variant="h5">{workTimeSummary.shifts}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">Bruttozeit</Typography>
              <Typography variant="h5">{formatMinutes(workTimeSummary.grossMinutes)}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">Pausen abgezogen</Typography>
              <Typography variant="h5">{formatMinutes(workTimeSummary.breakMinutes)}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">Nicht abgezogen</Typography>
              <Typography variant="h5">{formatMinutes(workTimeSummary.nonDeductibleBreakMinutes || 0)}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">Offline</Typography>
              <Typography variant="h5">{formatMinutes(workTimeSummary.offlineMinutes)}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">Netto</Typography>
              <Typography variant="h5">{formatMinutes(workTimeSummary.netMinutes)}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">Überstunden</Typography>
              <Typography variant="h5">{formatMinutes(workTimeSummary.overtimeMinutes)}</Typography>
            </Paper>
          </Grid>
        </Grid>

        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Datum</TableCell>
                <TableCell>Fahrer</TableCell>
                <TableCell>Schicht</TableCell>
                <TableCell>Start</TableCell>
                <TableCell>Ende</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Brutto</TableCell>
                <TableCell align="right">Pausen abgezogen</TableCell>
                <TableCell align="right">Nicht abgezogen</TableCell>
                <TableCell align="right">Offline</TableCell>
                <TableCell align="right">Netto</TableCell>
                <TableCell align="right">Überstunden</TableCell>
                <TableCell>Pausentypen</TableCell>
                <TableCell align="right">Aktionen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {workTimeLoading ? (
                <TableRow>
                  <TableCell colSpan={14} align="center">Arbeitszeiten werden geladen...</TableCell>
                </TableRow>
              ) : filteredWorkTimeRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={14} align="center">Keine Arbeitszeiten im Zeitraum</TableCell>
                </TableRow>
              ) : (
                filteredWorkTimeRows.map(row => (
                  <TableRow key={row.id}>
                    <TableCell>{row.dateKey}</TableCell>
                    <TableCell>{row.displayDriverName}</TableCell>
                    <TableCell>#{row.shiftNumber}</TableCell>
                    <TableCell>{formatDateTime(row.shiftStart)}</TableCell>
                    <TableCell>{row.isActive ? 'läuft' : formatDateTime(row.shiftEnd)}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <Chip
                          size="small"
                          color={row.isActive ? 'success' : 'default'}
                          label={row.isActive ? 'Aktiv' : 'Abgeschlossen'}
                        />
                        {row.hasOpenItems && !row.isActive && (
                          <Chip size="small" color="warning" label="Offen" />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">{formatMinutes(row.grossMinutes)}</TableCell>
                    <TableCell align="right">{formatMinutes(row.breakMinutes)}</TableCell>
                    <TableCell align="right">{formatMinutes(row.nonDeductibleBreakMinutes || 0)}</TableCell>
                    <TableCell align="right">{formatMinutes(row.offlineMinutes)}</TableCell>
                    <TableCell align="right">{formatMinutes(row.netMinutes)}</TableCell>
                    <TableCell align="right">
                      <Typography
                        component="span"
                        color={row.overtimeMinutes > 0 ? 'warning.main' : 'text.primary'}
                        fontWeight={row.overtimeMinutes > 0 ? 700 : 400}
                      >
                        {formatMinutes(row.overtimeMinutes)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {(row.breakTypes || []).length > 0 ? (
                          row.breakTypes.map(type => (
                            <Chip key={type} size="small" label={type} variant="outlined" />
                          ))
                        ) : (
                          <Typography variant="body2" color="text.secondary">-</Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        title="Schichtdetails anzeigen"
                        onClick={() => handleOpenWorkTimeDetails(row)}
                      >
                        <ViewIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {renderWorkTimeDialogs()}
      </Box>
    );
  };
  
  // Umsatz-Tab-Inhalt
  const renderRevenueTab = () => {
    // TODO: Detaillierte Umsatzanalyse implementieren
    return (
      <Typography variant="body1">
        Umsatzanalyse wird implementiert...
      </Typography>
    );
  };

  const renderOperatorsTab = () => {
    const total = operatorReportRows.reduce((sum, ride) => sum + ride.billableFare, 0);
    const crossOperatorCount = operatorReportRows.filter(ride => ride.mediationType === 'cross_operator').length;
    const baseOperatorOptions = operators.length > 0
      ? operators
      : Array.from(new Map(completedReportRides.flatMap(ride => [
          [ride.createdByOperatorId || ride.preferredOperatorId, ride.createdByOperatorName || ride.preferredOperatorName],
          [ride.driverOperatorId || ride.vehicleOperatorId, ride.driverOperatorName]
        ]).filter(([id]) => id).map(([id, name]) => [id, { id, name: name || id }])).values());
    const hasCentralCreatedRides = completedReportRides.some(ride => getCreatedByCentralName(ride));
    const hasCentralExecutedRides = completedReportRides.some(ride => (
      !ride.driverOperatorId &&
      !ride.vehicleOperatorId &&
      (ride.driverId || ride.driverVehicleId || ride.driverName || ride.driverLicensePlate)
    ));
    const operatorOptions = hasCentralCreatedRides || hasCentralExecutedRides
      ? [{ id: CENTRAL_CREATED_OPERATOR_ID, name: 'Hauptzentrale' }, ...baseOperatorOptions]
      : baseOperatorOptions;

    return (
      <Box>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 3 }}>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Auftraggeber</InputLabel>
            <Select
              label="Auftraggeber"
              value={selectedCreatedOperatorId}
              onChange={(event) => setSelectedCreatedOperatorId(event.target.value)}
            >
              <MenuItem value="all">Alle</MenuItem>
              {operatorOptions.map(operator => (
                <MenuItem key={operator.id} value={operator.id}>{operator.name || operator.id}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Ausführer</InputLabel>
            <Select
              label="Ausführer"
              value={selectedDriverOperatorId}
              onChange={(event) => setSelectedDriverOperatorId(event.target.value)}
            >
              <MenuItem value="all">Alle</MenuItem>
              {operatorOptions.map(operator => (
                <MenuItem key={operator.id} value={operator.id}>{operator.name || operator.id}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Vermittlung</InputLabel>
            <Select
              label="Vermittlung"
              value={selectedMediationStatus}
              onChange={(event) => setSelectedMediationStatus(event.target.value)}
            >
              <MenuItem value="all">Alle</MenuItem>
              <MenuItem value="cross_operator">An fremden Unternehmer vermittelt</MenuItem>
              <MenuItem value="same_operator">Eigen gefahren</MenuItem>
              <MenuItem value="central">Hauptzentrale</MenuItem>
              <MenuItem value="unknown">Unklar</MenuItem>
            </Select>
          </FormControl>
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleExportCsv}>
            CSV exportieren
          </Button>
          <Button variant="outlined" startIcon={<PrintIcon />} onClick={handleExportPdf}>
            PDF exportieren
          </Button>
        </Stack>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">Fahrten</Typography>
              <Typography variant="h5">{operatorReportRows.length}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">Vermittelte Fahrten</Typography>
              <Typography variant="h5">{crossOperatorCount}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">Umsatz</Typography>
              <Typography variant="h5">{formatMoney(total)}</Typography>
            </Paper>
          </Grid>
        </Grid>

        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Datum</TableCell>
                <TableCell>Auftraggeber</TableCell>
                <TableCell>Ausführer</TableCell>
                <TableCell>Fahrer</TableCell>
                <TableCell>Start</TableCell>
                <TableCell>Ziel</TableCell>
                <TableCell align="right">Betrag</TableCell>
                <TableCell>Art</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {operatorReportRows.map(ride => (
                <TableRow key={ride.id}>
                  <TableCell>{formatDateTime(ride.refDate)}</TableCell>
                  <TableCell>{ride.createdOperatorName}</TableCell>
                  <TableCell>{ride.driverOperatorName}</TableCell>
                  <TableCell>{ride.driverName || '-'}</TableCell>
                  <TableCell>{ride.start || '-'}</TableCell>
                  <TableCell>{ride.destination || '-'}</TableCell>
                  <TableCell align="right">{formatMoney(ride.billableFare)}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={ride.mediationType === 'cross_operator' ? 'warning' : ride.mediationType === 'central' ? 'primary' : 'default'}
                      label={getMediationLabel(ride.mediationType)}
                    />
                  </TableCell>
                </TableRow>
              ))}
              {operatorReportRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center">Keine abgeschlossenen Unternehmer-Fahrten im Zeitraum</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };
  
  // Hauptinhalt basierend auf dem ausgewählten Tab
  const tabContent = useMemo(() => {
    switch (currentTab) {
      case TAB_OVERVIEW:  return renderOverviewTab;
      case TAB_RIDES:     return renderRidesTab();
      case TAB_DRIVERS:   return renderDriversTab();
      case TAB_WORK_TIMES: return renderWorkTimesTab();
      case TAB_REVENUE:   return renderRevenueTab();
      case TAB_OPERATORS: return renderOperatorsTab();
      default:            return renderOverviewTab;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentTab,
    renderOverviewTab,
    driverReportRows,
    operatorReportRows,
    filteredWorkTimeRows,
    workTimeSummary,
    workTimeLoading,
    workTimeError,
    selectedDriverId,
    selectedCreatedOperatorId,
    selectedDriverOperatorId,
    selectedMediationStatus,
    selectedWorkTimeDriverId,
    selectedWorkTimeStatus,
    selectedWorkTimeBreakType,
    workTimeDriverOptions,
    workTimeBreakTypes,
    selectedWorkTimeRow,
    breakDialogOpen,
    editingBreak,
    breakForm,
    breakActionError,
    breakSaving,
    shiftDialogOpen,
    shiftForm,
    shiftActionError,
    shiftSaving
  ]);
  
  return (
    <Container maxWidth="xl">
      <Paper elevation={2} sx={{ mt: 2, p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" component="h1">
            Berichte und Analysen: {dateRangeTitle}
          </Typography>
          
          <Box>
            <IconButton onClick={handleFilterClick} title="Zeitraum auswählen">
              <FilterIcon />
            </IconButton>
            <IconButton onClick={handlePrint} title="Drucken">
              <PrintIcon />
            </IconButton>
            <IconButton onClick={handleExportCsv} title="Exportieren">
              <DownloadIcon />
            </IconButton>
            
            <Menu
              anchorEl={filterAnchorEl}
              open={Boolean(filterAnchorEl)}
              onClose={handleFilterClose}
            >
              <MenuItem onClick={() => handleFilterSelect(FILTER_DAY)}>Heute</MenuItem>
              <MenuItem onClick={() => handleFilterSelect(FILTER_WEEK)}>Diese Woche</MenuItem>
              <MenuItem onClick={() => handleFilterSelect(FILTER_MONTH)}>Dieser Monat</MenuItem>
              <MenuItem onClick={() => handleFilterSelect(FILTER_LAST_MONTH)}>Letzter Monat</MenuItem>
              <MenuItem onClick={() => handleFilterSelect(FILTER_QUARTER)}>Dieses Quartal</MenuItem>
              <MenuItem onClick={() => handleFilterSelect(FILTER_YEAR)}>Dieses Jahr</MenuItem>
              <Divider />
              <MenuItem onClick={() => handleFilterSelect(FILTER_CUSTOM)}>Eigener Zeitraum</MenuItem>
            </Menu>
          </Box>
        </Box>

        <Dialog open={customRangeDialogOpen} onClose={handleCloseCustomRangeDialog} fullWidth maxWidth="xs">
          <DialogTitle>Eigener Zeitraum</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="Von"
                type="date"
                value={customRangeDraftStart}
                onChange={(event) => setCustomRangeDraftStart(event.target.value)}
                InputLabelProps={{ shrink: true }}
                error={customRangeDraftInvalid}
                fullWidth
              />
              <TextField
                label="Bis"
                type="date"
                value={customRangeDraftEnd}
                onChange={(event) => setCustomRangeDraftEnd(event.target.value)}
                InputLabelProps={{ shrink: true }}
                error={customRangeDraftInvalid}
                helperText={customRangeDraftInvalid ? 'Bitte einen gültigen Zeitraum wählen.' : ''}
                fullWidth
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseCustomRangeDialog}>Abbrechen</Button>
            <Button
              variant="contained"
              onClick={handleApplyCustomRange}
              disabled={customRangeDraftInvalid}
            >
              Anwenden
            </Button>
          </DialogActions>
        </Dialog>
        
        <Divider sx={{ mb: 2 }} />
        
        <Box sx={{ mb: 3 }}>
          <Tabs 
            value={currentTab} 
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Übersicht" />
            <Tab label="Fahrtenanalyse" />
            <Tab label="Fahrerleistung" />
            <Tab label="Arbeitszeiten" />
            <Tab label="Umsatzanalyse" />
            <Tab label="Unternehmer" />
          </Tabs>
        </Box>
        
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
            <Typography variant="body1">Daten werden geladen...</Typography>
          </Box>
        ) : error ? (
          <Box sx={{ mt: 2 }}>
            <Typography color="error">
              Fehler beim Laden der Daten: {error}
            </Typography>
            <Button 
              variant="contained" 
              sx={{ mt: 2 }} 
              onClick={() => setDateRange(dateRange)}
            >
              Erneut versuchen
            </Button>
          </Box>
        ) : (
          tabContent
        )}
      </Paper>
    </Container>
  );
};

export default ReportsPage;
